import type { RatingBreakdown } from '@/types'

/**
 * 计算加权平均评分
 * 0分、1分、5分权重为0.5，其他权重为1
 */
export function calculateWeightedRating(breakdown: RatingBreakdown): number {
  const { zeroStars, oneStars, twoStars, threeStars, fourStars, fiveStars } = breakdown
  
  // 计算总分（0分、1分、5分权重为0.5，其他权重为1）
  const totalScore = 
    (zeroStars * 0 * 0.5) +     // 0分权重0.5倍
    (oneStars * 1 * 0.5) +      // 1分权重0.5倍
    (twoStars * 2) +            // 2分权重1倍
    (threeStars * 3) +          // 3分权重1倍
    (fourStars * 4) +           // 4分权重1倍
    (fiveStars * 5 * 0.5)       // 5分权重0.5倍
  
  // 计算总权重（0分、1分、5分权重为0.5，其他权重为1）
  const totalWeight = 
    (zeroStars * 0.5) +         // 0分权重0.5倍
    (oneStars * 0.5) +          // 1分权重0.5倍
    twoStars +                  // 2分权重1倍
    threeStars +                // 3分权重1倍
    fourStars +                 // 4分权重1倍
    (fiveStars * 0.5)           // 5分权重0.5倍
  
  if (totalWeight === 0) return 0
  
  return Math.round((totalScore / totalWeight) * 10) / 10
}

/**
 * 计算总评分人数
 */
export function calculateTotalRatings(breakdown: RatingBreakdown): number {
  const { zeroStars, oneStars, twoStars, threeStars, fourStars, fiveStars } = breakdown
  return zeroStars + oneStars + twoStars + threeStars + fourStars + fiveStars
}

/**
 * 生成模拟评分分布
 */
export function generateMockRatingBreakdown(rating: number): RatingBreakdown {
  const totalRatings = Math.floor(Math.random() * 500) + 100 // 100-600人评分
  
  // 根据评分生成合理的分布
  let zeroStars = 0, oneStars = 0, twoStars = 0, threeStars = 0, fourStars = 0, fiveStars = 0
  
  if (rating >= 4.5) {
    // 高评分
    fiveStars = Math.floor(totalRatings * (0.4 + Math.random() * 0.3))
    fourStars = Math.floor(totalRatings * (0.2 + Math.random() * 0.2))
    threeStars = Math.floor(totalRatings * (0.1 + Math.random() * 0.1))
    twoStars = Math.floor(totalRatings * (0.05 + Math.random() * 0.05))
    oneStars = Math.floor(totalRatings * (0.02 + Math.random() * 0.03))
    zeroStars = totalRatings - fiveStars - fourStars - threeStars - twoStars - oneStars
  } else if (rating >= 3.5) {
    // 中高评分
    fiveStars = Math.floor(totalRatings * (0.2 + Math.random() * 0.2))
    fourStars = Math.floor(totalRatings * (0.3 + Math.random() * 0.2))
    threeStars = Math.floor(totalRatings * (0.2 + Math.random() * 0.2))
    twoStars = Math.floor(totalRatings * (0.1 + Math.random() * 0.1))
    oneStars = Math.floor(totalRatings * (0.05 + Math.random() * 0.05))
    zeroStars = totalRatings - fiveStars - fourStars - threeStars - twoStars - oneStars
  } else if (rating >= 2.5) {
    // 中等评分
    fiveStars = Math.floor(totalRatings * (0.1 + Math.random() * 0.1))
    fourStars = Math.floor(totalRatings * (0.2 + Math.random() * 0.2))
    threeStars = Math.floor(totalRatings * (0.3 + Math.random() * 0.2))
    twoStars = Math.floor(totalRatings * (0.2 + Math.random() * 0.2))
    oneStars = Math.floor(totalRatings * (0.1 + Math.random() * 0.1))
    zeroStars = totalRatings - fiveStars - fourStars - threeStars - twoStars - oneStars
  } else {
    // 低评分
    fiveStars = Math.floor(totalRatings * (0.05 + Math.random() * 0.05))
    fourStars = Math.floor(totalRatings * (0.1 + Math.random() * 0.1))
    threeStars = Math.floor(totalRatings * (0.2 + Math.random() * 0.2))
    twoStars = Math.floor(totalRatings * (0.2 + Math.random() * 0.2))
    oneStars = Math.floor(totalRatings * (0.2 + Math.random() * 0.2))
    zeroStars = totalRatings - fiveStars - fourStars - threeStars - twoStars - oneStars
  }
  
  return {
    zeroStars: Math.max(0, zeroStars),
    oneStars: Math.max(0, oneStars),
    twoStars: Math.max(0, twoStars),
    threeStars: Math.max(0, threeStars),
    fourStars: Math.max(0, fourStars),
    fiveStars: Math.max(0, fiveStars)
  }
}